/*
 * <collection-view-all>
 * Loads every product in the collection onto the current page.
 *
 * On click, it requests each paginated page through the Section Rendering API
 * (preserving any active filters/sort from the current URL), collects the
 * product cards in page order, and replaces the grid contents with the full
 * set. The pagination and the button are then removed. The server-rendered
 * pagination remains the fallback when JavaScript is unavailable.
 */
class CollectionViewAll extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    this.spinner = this.querySelector('.loading__spinner');
    if (this.button) {
      this.button.addEventListener('click', () => this.loadAll());
    }
  }

  get totalPages() {
    return parseInt(this.dataset.totalPages, 10) || 1;
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  setLoading(isLoading) {
    this.classList.toggle('collection-view-all--loading', isLoading);
    this.button.toggleAttribute('disabled', isLoading);
    this.button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    if (this.spinner) this.spinner.classList.toggle('hidden', !isLoading);
  }

  // Build a Section Rendering API URL for a given page, carrying over the
  // current filters and sort so "View all" respects the customer's selection.
  buildPageUrl(page) {
    const params = new URLSearchParams(window.location.search);
    params.set('page', page);
    params.set('section_id', this.sectionId);
    return `${window.location.pathname}?${params.toString()}`;
  }

  async fetchPageItems(page) {
    const response = await fetch(this.buildPageUrl(page));
    if (!response.ok) throw new Error(`Failed to load page ${page}: ${response.status}`);
    const text = await response.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    return Array.from(doc.querySelectorAll('#product-grid > li'));
  }

  async loadAll() {
    if (this.button.hasAttribute('disabled')) return;

    const grid = document.getElementById('product-grid');
    if (!grid) return;

    this.setLoading(true);

    const pageNumbers = [];
    for (let page = 1; page <= this.totalPages; page += 1) {
      pageNumbers.push(page);
    }

    try {
      // Fetch all pages in parallel, then append in page order so the full
      // collection is rebuilt regardless of which page the customer was on.
      const results = await Promise.all(pageNumbers.map((page) => this.fetchPageItems(page)));
      const fragment = document.createDocumentFragment();

      results.flat().forEach((item) => {
        // Cancel reveal-on-scroll animation so newly inserted cards stay visible.
        item.classList.add('scroll-trigger--cancel');
        item
          .querySelectorAll('.scroll-trigger')
          .forEach((element) => element.classList.add('scroll-trigger--cancel'));
        fragment.appendChild(item);
      });

      grid.replaceChildren(fragment);

      // Re-apply Badge It badges to the freshly injected cards. Badge It badges
      // cards on page load and doesn't see ones added afterward, so re-run its
      // global. No duplicates: the whole grid was replaced with fresh, un-badged
      // cards. This mirrors how Badge It re-runs itself after a sort change.
      if (typeof window.myBadgeIt === 'function') {
        window.myBadgeIt();
      }

      const collection = grid.closest('.collection');
      const paginationWrapper = collection ? collection.querySelector('.pagination-wrapper') : null;
      if (paginationWrapper) paginationWrapper.remove();

      // Anchor the customer back to the top of the page now that every product is
      // loaded, so they can scroll through the full set from the start.
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });

      this.remove();
    } catch (error) {
      console.error('[collection-view-all]', error);
      // Re-enable the button so the customer can retry; pagination is untouched.
      this.setLoading(false);
    }
  }
}

customElements.define('collection-view-all', CollectionViewAll);
